import React, { createContext, useContext, useState, useCallback, useRef } from 'react';
import { Lightbulb, RefreshCw, Sparkles, TrendingUp } from 'lucide-react';

/**
 * Learning Intelligence Layer
 * 
 * Adaptive learning system that responds to student performance:
 * - Miss 2 questions → Show another worked example
 * - Hesitation (>15s on a question) → Show hint layer before marking wrong
 * - Strong performance (3+ correct in a row) → Skip extra practice, move faster
 * 
 * This turns static lessons into intelligent tutoring.
 */

// Context for sharing intelligence state across lesson components
const LearningIntelligenceContext = createContext(null);

// Time threshold for "hesitation" detection (milliseconds)
const HESITATION_THRESHOLD = 15000; // 15 seconds

export const LearningIntelligenceProvider = ({ children, lessonId }) => {
  // Track consecutive wrong answers
  const [consecutiveWrong, setConsecutiveWrong] = useState(0);
  
  // Track consecutive correct answers (for fast-tracking)
  const [consecutiveCorrect, setConsecutiveCorrect] = useState(0);
  
  // Track if we should show an extra worked example
  const [showExtraExample, setShowExtraExample] = useState(false);
  const [extraExampleTopic, setExtraExampleTopic] = useState(null);
  
  // Track if hint should be shown (hesitation detected)
  const [showHintLayer, setShowHintLayer] = useState(false);
  const [currentHint, setCurrentHint] = useState(null);
  
  // Track if student is in "fast track" mode
  const [fastTrackMode, setFastTrackMode] = useState(false);
  
  // Timer refs for hesitation detection
  const questionStartTime = useRef(null);
  const hesitationTimer = useRef(null);
  
  // Performance stats for this session
  const [stats, setStats] = useState({
    totalQuestions: 0,
    correctAnswers: 0,
    hintsShown: 0,
    extraExamplesShown: 0,
    fastTrackSkips: 0
  });
  
  // Called when a new question/interactive block starts
  const onQuestionStart = useCallback((blockData) => {
    questionStartTime.current = Date.now();
    setShowHintLayer(false);
    
    // Clear any existing hesitation timer
    if (hesitationTimer.current) {
      clearTimeout(hesitationTimer.current);
    }
    
    // Set up hesitation detection
    if (blockData?.hint) {
      hesitationTimer.current = setTimeout(() => {
        // Student is taking a long time - show hint
        setShowHintLayer(true);
        setCurrentHint(blockData.hint);
        setStats(prev => ({ ...prev, hintsShown: prev.hintsShown + 1 }));
      }, HESITATION_THRESHOLD);
    }
  }, []);
  
  // Called when student answers a question
  const onQuestionAnswer = useCallback((isCorrect, blockData) => {
    // Clear hesitation timer
    if (hesitationTimer.current) {
      clearTimeout(hesitationTimer.current);
    }
    
    const responseTime = Date.now() - (questionStartTime.current || Date.now());
    
    setStats(prev => ({
      ...prev,
      totalQuestions: prev.totalQuestions + 1,
      correctAnswers: prev.correctAnswers + (isCorrect ? 1 : 0)
    }));
    
    if (isCorrect) {
      // Correct answer
      setConsecutiveWrong(0);
      setConsecutiveCorrect(prev => prev + 1);
      setShowHintLayer(false);
      setShowExtraExample(false);
      
      // Check for fast-track mode (3+ correct in a row)
      if (consecutiveCorrect + 1 >= 3 && !fastTrackMode) {
        setFastTrackMode(true);
      }
    } else {
      // Wrong answer
      setConsecutiveCorrect(0);
      setConsecutiveWrong(prev => prev + 1);
      setFastTrackMode(false);
      
      // Check if we should show extra worked example (2+ wrong)
      if (consecutiveWrong + 1 >= 2) {
        setShowExtraExample(true);
        setExtraExampleTopic(blockData?.topic || 'this concept');
        setStats(prev => ({ ...prev, extraExamplesShown: prev.extraExamplesShown + 1 }));
      }
    }
  }, [consecutiveWrong, consecutiveCorrect, fastTrackMode]);
  
  // Called to dismiss the extra example
  const dismissExtraExample = useCallback(() => {
    setShowExtraExample(false);
    setConsecutiveWrong(0);
  }, []);
  
  // Called to dismiss hint layer
  const dismissHint = useCallback(() => {
    setShowHintLayer(false);
  }, []);
  
  // Check if current block can be skipped (fast-track mode)
  const canSkipBlock = useCallback((blockType) => {
    // Only skip extra practice blocks in fast-track mode
    // Never skip challenge or new concept blocks
    const skippableTypes = ['question', 'matching', 'fill_blank'];
    return fastTrackMode && skippableTypes.includes(blockType);
  }, [fastTrackMode]);
  
  // Skip a block and track it
  const skipBlock = useCallback(() => {
    setStats(prev => ({ ...prev, fastTrackSkips: prev.fastTrackSkips + 1 }));
  }, []);
  
  // Reset intelligence state for new lesson
  const resetIntelligence = useCallback(() => {
    setConsecutiveWrong(0);
    setConsecutiveCorrect(0);
    setShowExtraExample(false);
    setShowHintLayer(false);
    setFastTrackMode(false);
    setStats({
      totalQuestions: 0,
      correctAnswers: 0,
      hintsShown: 0,
      extraExamplesShown: 0,
      fastTrackSkips: 0
    });
  }, []);
  
  const value = {
    // State
    consecutiveWrong,
    consecutiveCorrect,
    showExtraExample,
    extraExampleTopic,
    showHintLayer,
    currentHint,
    fastTrackMode,
    stats,
    
    // Actions
    onQuestionStart,
    onQuestionAnswer,
    dismissExtraExample,
    dismissHint,
    canSkipBlock,
    skipBlock,
    resetIntelligence
  };
  
  return (
    <LearningIntelligenceContext.Provider value={value}>
      {children}
    </LearningIntelligenceContext.Provider>
  );
};

// Hook to use the learning intelligence
export const useLearningIntelligence = () => {
  const context = useContext(LearningIntelligenceContext);
  if (!context) {
    // Return a no-op version if not wrapped in provider
    return {
      consecutiveWrong: 0,
      consecutiveCorrect: 0,
      showExtraExample: false,
      showHintLayer: false,
      fastTrackMode: false,
      stats: {},
      onQuestionStart: () => {},
      onQuestionAnswer: () => {},
      dismissExtraExample: () => {},
      dismissHint: () => {},
      canSkipBlock: () => false,
      skipBlock: () => {},
      resetIntelligence: () => {}
    };
  }
  return context;
};

/**
 * Extra Worked Example Modal
 * Shows when student misses 2 questions in a row
 * Tone: Supportive, "let's try another way" - NOT "you got it wrong"
 */
export const ExtraExampleModal = ({ topic, onDismiss, workedExample }) => {
  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <div className="glass-panel rounded-3xl max-w-lg w-full p-6 animate-slide-up">
        {/* Header - Supportive, not corrective */}
        <div className="flex items-center gap-3 mb-4">
          <div className="w-12 h-12 rounded-full bg-amber-500/20 flex items-center justify-center">
            <RefreshCw className="w-6 h-6 text-amber-400" />
          </div>
          <div>
            <h3 className="text-white font-bold text-lg">Let's Look at This a Different Way</h3>
            <p className="text-slate-400 text-sm">Sometimes a new angle helps it click</p>
          </div>
        </div>
        
        {/* Encouraging message - confidence-preserving */}
        <div className="bg-amber-500/10 border border-amber-500/30 rounded-xl p-4 mb-4">
          <p className="text-amber-200 text-sm">
            This one's tricky! Even experts needed to see {topic} explained a few different ways. Let me walk through it with you.
          </p>
        </div>
        
        {/* Worked Example Content */}
        {workedExample ? (
          <div className="space-y-4 mb-6">
            {workedExample.setup && (
              <div className="bg-slate-800/50 rounded-xl p-4">
                <p className="text-slate-400 text-xs uppercase tracking-wider mb-2">Here's how to think about it:</p>
                <p className="text-white">{workedExample.setup}</p>
              </div>
            )}
            
            {workedExample.steps && (
              <div className="space-y-2">
                {workedExample.steps.map((step, idx) => (
                  <div key={idx} className="flex items-start gap-3 bg-slate-900/50 rounded-lg p-3">
                    <div className="w-6 h-6 rounded-full bg-cyan-500/20 flex items-center justify-center flex-shrink-0">
                      <span className="text-cyan-400 text-sm font-bold">{idx + 1}</span>
                    </div>
                    <p className="text-slate-200 text-sm">{step}</p>
                  </div>
                ))}
              </div>
            )}
            
            {workedExample.conclusion && (
              <div className="bg-green-500/10 border border-green-500/30 rounded-xl p-4 text-center">
                <p className="text-green-400 text-sm font-semibold">{workedExample.conclusion}</p>
              </div>
            )}
          </div>
        ) : (
          <div className="bg-slate-800/50 rounded-xl p-6 mb-6 text-center">
            <Lightbulb className="w-8 h-8 text-amber-400 mx-auto mb-3" />
            <p className="text-slate-300">
              Remember the patterns and strategies from earlier - they'll help here too!
            </p>
          </div>
        )}
        
        {/* Action button - encouraging */}
        <button
          onClick={onDismiss}
          className="w-full bg-gradient-to-r from-amber-500 to-orange-500 text-white rounded-xl py-3 font-semibold hover:opacity-90 transition-opacity"
          data-testid="extra-example-continue-btn"
        >
          Okay, I'm ready to try again!
        </button>
      </div>
    </div>
  );
};

/**
 * Hint Layer Component
 * Shows when student hesitates on a question
 * Tone: Supportive "need a little help?" - keeps confidence intact
 */
export const HintLayer = ({ hint, onDismiss, onTryAgain }) => {
  if (!hint) return null;
  
  return (
    <div className="animate-slide-up bg-gradient-to-r from-indigo-500/20 to-purple-500/20 border border-indigo-500/40 rounded-xl p-4 mb-4">
      <div className="flex items-start gap-3">
        <div className="w-8 h-8 rounded-full bg-indigo-500/30 flex items-center justify-center flex-shrink-0">
          <Lightbulb className="w-4 h-4 text-indigo-300" />
        </div>
        <div className="flex-1">
          <p className="text-indigo-300 text-xs uppercase tracking-wider mb-1">Need a little help?</p>
          <p className="text-white text-sm">{hint}</p>
          <p className="text-green-400 text-xs mt-2 font-medium">You've got this — take your time and try again!</p>
        </div>
        <button 
          onClick={onDismiss}
          className="text-slate-400 hover:text-white text-sm"
          aria-label="Close hint"
        >
          ✕
        </button>
      </div>
    </div>
  );
};

/**
 * Fast Track Indicator
 * Shows when student is doing well and can skip extra practice
 * Added 1.5s delay so it doesn't feel rushed
 */
export const FastTrackIndicator = ({ onSkip, blockType }) => {
  const [showSkip, setShowSkip] = React.useState(false);
  
  // Add slight delay before showing skip option
  React.useEffect(() => {
    const timer = setTimeout(() => {
      setShowSkip(true);
    }, 1500); // 1.5 second delay
    return () => clearTimeout(timer);
  }, []);
  
  if (!showSkip) return null;
  
  return (
    <div className="animate-slide-up bg-gradient-to-r from-green-500/20 to-emerald-500/20 border border-green-500/40 rounded-xl p-4 mb-4">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="w-8 h-8 rounded-full bg-green-500/30 flex items-center justify-center">
            <TrendingUp className="w-4 h-4 text-green-400" />
          </div>
          <div>
            <p className="text-green-400 text-sm font-semibold">You're doing great!</p>
            <p className="text-slate-400 text-xs">Feel free to skip this extra practice</p>
          </div>
        </div>
        <button
          onClick={onSkip}
          className="bg-green-500/20 hover:bg-green-500/30 text-green-400 rounded-lg px-4 py-2 text-sm font-medium transition-colors"
          data-testid="fast-track-skip-btn"
        >
          Skip →
        </button>
      </div>
    </div>
  );
};

/**
 * Intelligence Stats Display (for reward/completion screen)
 */
export const IntelligenceStats = ({ stats }) => {
  if (!stats || stats.totalQuestions === 0) return null;
  
  const accuracy = Math.round((stats.correctAnswers / stats.totalQuestions) * 100);
  
  return (
    <div className="bg-slate-800/50 border border-slate-700/50 rounded-xl p-4 mt-4">
      <div className="flex items-center gap-2 mb-3">
        <Sparkles className="w-4 h-4 text-purple-400" />
        <span className="text-slate-300 text-sm font-medium">Learning Insights</span>
      </div>
      <div className="grid grid-cols-2 gap-3 text-center">
        <div className="bg-slate-900/50 rounded-lg p-2">
          <p className="text-2xl font-bold text-white">{accuracy}%</p>
          <p className="text-xs text-slate-400">Accuracy</p>
        </div>
        <div className="bg-slate-900/50 rounded-lg p-2">
          <p className="text-2xl font-bold text-white">{stats.totalQuestions}</p>
          <p className="text-xs text-slate-400">Questions</p>
        </div>
      </div>
      {stats.fastTrackSkips > 0 && (
        <p className="text-xs text-green-400 mt-2 text-center">
          Skipped {stats.fastTrackSkips} practice questions - you're a fast learner!
        </p>
      )}
      {stats.extraExamplesShown > 0 && (
        <p className="text-xs text-amber-400 mt-2 text-center">
          Extra examples helped you master {stats.extraExamplesShown} concepts
        </p>
      )}
    </div>
  );
};

// CSS animation for slide-up effect (add to global styles or use inline)
const styleSheet = `
@keyframes slide-up {
  from {
    opacity: 0;
    transform: translateY(10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.animate-slide-up {
  animation: slide-up 0.3s ease-out forwards;
}
`;

// Inject styles if not already present
if (typeof document !== 'undefined' && !document.getElementById('learning-intelligence-styles')) {
  const style = document.createElement('style');
  style.id = 'learning-intelligence-styles';
  style.textContent = styleSheet;
  document.head.appendChild(style);
}

export default LearningIntelligenceProvider;
